import {
    SpaceBetween,
    BreadcrumbGroup,
    Flashbar,
    Header,
    Container,
    Form,
    FormField,
    Input,
    Button,
    Multiselect,
} from '@amzn/awsui-components-console';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import InputWithValidation from '../../../lib/InputWithValidation';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { displayErrorContent, isValidEmailInput, isValidInput } from '../../../lib/utils';
import { configureLab } from '../apis';
import { CLIENT_ERROR, SERVER_ERROR } from '../../../lib/constants';
import AlarmRulesTable from '../alarm-rules/AlarmRulesTable';

export default function ConfigureLab(props) {
    const { id } = useParams();
    const history = useHistory();
    const { state } = props.location;
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [ownerError, setOwnerError] = React.useState('');
    const [owner, setOwner] = React.useState('');
    const [accessGroup, setAccessGroup] = React.useState('');
    const [accessGroupError, setAccessGroupError] = React.useState('');
    const [dotsPoolName, setDotsPoolName] = React.useState('');
    const [monitorEnabled, setMonitorEnabled] = React.useState(true);
    const [ticketCategory, setTicketCategory] = React.useState('');
    const [ticketCategoryError, setTicketCategoryError] = React.useState('');
    const [ticketType, setTicketType] = React.useState('');
    const [ticketTypeError, setTicketTypeError] = React.useState('');
    const [ticketItem, setTicketItem] = React.useState('');
    const [ticketItemError, setTicketItemError] = React.useState('');
    const [ticketGroup, setTicketGroup] = React.useState('');
    const [ticketGroupError, setTicketGroupError] = React.useState('');
    const [confirmLoading, setConfirmLoading] = React.useState(false);
    const [alarmRules, setAlarmRules] = useState(undefined);

    // Saved lab data from original state — used by alarm rules operations
    // so they don't accidentally persist unsaved form changes
    const savedLabData = useMemo(() => {
        if (!state) return {};
        return {
            labId: id,
            owner: state['owner'] || '',
            accessGroup: state['accessGroup'] || '',
            ticketCategory: state['Category'] || '',
            ticketType: state['Type'] || '',
            ticketItem: state['Item'] || '',
            ticketGroup: state['Group'] || '',
            dotsPoolName: state['dotsPoolName'] || '',
            monitorEnabled: state['monitorEnabled'] === undefined ? true : state['monitorEnabled'],
        };
    }, [id, state]);

    /**
     * set initial status
     */
    const setInitialStatus = () => {
        setConfirmLoading(true);
        setErrorMessage([]);
    };

    /**
     * validate input
     */
    const validateInput = () => {
        let errorflag = false;
        if (!isValidEmailInput(owner)) {
            setOwnerError('Owner should be valid and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(accessGroup)) {
            setAccessGroupError('Access group is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(ticketCategory)) {
            setTicketCategoryError('Category for ticket is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(ticketType)) {
            setTicketTypeError('Type for ticket is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(ticketItem)) {
            setTicketItemError('Item for ticket is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(ticketGroup)) {
            setTicketGroupError('Resolver group for ticket is required and should not be empty.');
            errorflag = true;
        }
        return !errorflag;
    };

    /**
     * click confirm button to trigger event
     */
    const onClick = async () => {
        let success = false;
        setInitialStatus();
        try {
            if (!validateInput()) {
                success = false;
            } else {
                await configureLab(
                    id,
                    owner,
                    accessGroup,
                    ticketCategory,
                    ticketType,
                    ticketItem,
                    ticketGroup,
                    dotsPoolName,
                    monitorEnabled,
                    alarmRules
                );
                success = true;
            }
        } catch (err) {
            const errResponse = err.response;
            if (errResponse && errResponse.data && errResponse.status === 400) {
                const errorException = errResponse.data.errorException || 'Client Error';
                const errorMessage = errResponse.data.errorMessage || errResponse.data;
                displayErrorContent(errorException, errorMessage, setErrorMessage);
            } else {
                displayErrorContent(
                    SERVER_ERROR,
                    'Fail to configure this lab, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                    setErrorMessage
                );
            }
        }
        setConfirmLoading(false);
        if (success) {
            history.push('/lab-management/labs');
        }
    };

    useEffect(() => {
        if (!id || !state) {
            history.push('/lab-management/labs');
        } else {
            setOwner(state['owner'] || '');
            setAccessGroup(state['accessGroup'] || '');
            setDotsPoolName(state['dotsPoolName'] || '');
            setMonitorEnabled(state['monitorEnabled'] === undefined ? true : state['monitorEnabled']);
            setTicketCategory(state['Category'] || '');
            setTicketType(state['Type'] || '');
            setTicketItem(state['Item'] || '');
            setTicketGroup(state['Group'] || '');
            setAlarmRules(state['alarmRules']);
        }
    }, [id, state]);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Lab management', href: '#/lab-management/labs' },
                    { text: 'Configure', href: '#/lab-management/labs/configure' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <form onSubmit={(e) => e.preventDefault()}>
                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button formAction="none" href="#/lab-management/labs">
                                Back
                            </Button>
                            <Button formAction="none" loading={confirmLoading} onClick={onClick} variant="primary">
                                Confirm
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween direction="vertical" size="m">
                        <Container header={<Header variant="h2">Lab details: {id}</Header>}>
                            <SpaceBetween direction="vertical" size="m">
                                <FormField label="Lab ID">
                                    <Input value={id} disabled />
                                </FormField>
                                <InputWithValidation
                                    label="Owner"
                                    type="text"
                                    description="provide email address for the lab owner"
                                    errorText={ownerError}
                                    placeholder="Enter email address for lab owner"
                                    value={owner}
                                    setValue={setOwner}
                                    setError={setOwnerError}
                                ></InputWithValidation>
                                <InputWithValidation
                                    label="Access group"
                                    type="text"
                                    description="LDAP group that can manage hosts and devices in the lab"
                                    errorText={accessGroupError}
                                    placeholder="Enter a LDAP group that can manage hosts and devices in the lab"
                                    value={accessGroup}
                                    setValue={setAccessGroup}
                                    setError={setAccessGroupError}
                                ></InputWithValidation>
                                <FormField
                                    label={
                                        <span>
                                            DOTS pool name <i>- optional</i>{' '}
                                        </span>
                                    }
                                    description="All the devices in the lab will be automatically added into this DOTS pool"
                                >
                                    <Input
                                        placeholder="Enter optional DOTS pool name as default pool for devices in the lab"
                                        value={dotsPoolName}
                                        onChange={(event) => {
                                            setDotsPoolName(event.detail.value);
                                        }}
                                    />
                                </FormField>
                                <FormField
                                    label="Device and host disconnection monitoring"
                                    description="Enable automatic monitoring of device and host disconnections. When enabled, tickets will be sent to the configured lab CTI if devices or hosts become disconnected."
                                >
                                    <RadioGroup
                                        value={monitorEnabled ? 'enabled' : 'disabled'}
                                        onChange={({ detail }) => setMonitorEnabled(detail.value === 'enabled')}
                                        items={[
                                            { value: 'enabled', label: 'Enabled' },
                                            { value: 'disabled', label: 'Disabled' },
                                        ]}
                                    />
                                </FormField>
                                <InputWithValidation
                                    label="Ticket category"
                                    type="text"
                                    description="Provide category as part of CTI for ticketing, tickets will be sent to your team for any disconnected devices/hosts in the lab"
                                    errorText={ticketCategoryError}
                                    placeholder="Enter category as part of CTI information"
                                    value={ticketCategory}
                                    setValue={setTicketCategory}
                                    setError={setTicketCategoryError}
                                ></InputWithValidation>
                                <InputWithValidation
                                    label="Ticket type"
                                    type="text"
                                    description="Provide type as part of CTI for ticketing, tickets will be sent to your team for any disconnected devices/hosts in the lab"
                                    errorText={ticketTypeError}
                                    placeholder="Enter type as part of CTI information"
                                    value={ticketType}
                                    setValue={setTicketType}
                                    setError={setTicketTypeError}
                                ></InputWithValidation>
                                <InputWithValidation
                                    label="Ticket item"
                                    type="text"
                                    description="Provide item as part of CTI for ticketing, tickets will be sent to your team for any disconnected devices/hosts in the lab"
                                    errorText={ticketItemError}
                                    placeholder="Enter item as part of CTI information"
                                    value={ticketItem}
                                    setValue={setTicketItem}
                                    setError={setTicketItemError}
                                ></InputWithValidation>
                                <InputWithValidation
                                    label="Ticket resolver group"
                                    type="text"
                                    description="Provide resolver group as part of CTI for ticketing, tickets will be sent to your team for any disconnected devices/hosts in the lab"
                                    errorText={ticketGroupError}
                                    placeholder="Enter resolver group as part of CTI information"
                                    value={ticketGroup}
                                    setValue={setTicketGroup}
                                    setError={setTicketGroupError}
                                ></InputWithValidation>
                            </SpaceBetween>
                        </Container>
                        <Flashbar items={errorMessage} />
                        <AlarmRulesTable
                            labId={id}
                            alarmRules={alarmRules}
                            labData={savedLabData}
                            onRulesChange={setAlarmRules}
                        />
                    </SpaceBetween>
                </Form>
            </form>
        </SpaceBetween>
    );
}
